/**
 * Currency formatting for RFQ surfaces.
 *
 * Wraps the shared `useFormatters().formatCurrency` so every RFQ view
 * renders money the same way: the RFQ's own currency code, two decimals,
 * and — unlike the dashboard-oriented shared default — a currency symbol on
 * zero values too ("₹0" rather than a bare "0"), because RFQ totals are
 * read as amounts, not KPI tiles.
 */
import { useCallback } from 'react';
import { useFormatters } from '../../contexts/PreferencesContext';
import { localeForCurrency } from '../../utils/currencyLocale';
import { toNumber } from './rfqFormat';

/**
 * @param defaultCurrency - Currency used when a call passes none (falls back
 *   to the user's preference currency when omitted)
 * @returns `formatMoney(value, currency?, decimals?)`
 */
export function useRfqMoney(defaultCurrency?: string) {
  const { formatCurrency, preferences } = useFormatters();

  return useCallback(
    (value: unknown, currency?: string, decimals = 2): string => {
      const code = currency || defaultCurrency || preferences.currency;
      const amount = toNumber(value);
      if (amount === 0) {
        try {
          return new Intl.NumberFormat(localeForCurrency(code), {
            style: 'currency',
            currency: code,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(0).replace('US$', '$');
        } catch {
          return '0';
        }
      }
      try {
        return formatCurrency(amount, code, decimals);
      } catch {
        return `${code} ${amount.toFixed(decimals)}`;
      }
    },
    [formatCurrency, preferences.currency, defaultCurrency],
  );
}
